import { useState, useEffect } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { Box, CircularProgress, Typography, useTheme, useMediaQuery, Button, ButtonGroup } from '@mui/material';
import { tokens } from '../../theme';
import axios from 'axios';

const DecSupplyChart = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fullData, setFullData] = useState([]); // Store all data
  const [chartData, setChartData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('30'); // Default 30 days
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Custom theme for the line chart
  const customChartTheme = {
    axis: {
      ticks: {
        text: {
          fill: colors.grey[100],
          fontSize: isMobile ? 9 : 11
        }
      },
      legend: {
        text: {
          fill: colors.grey[100],
          fontSize: isMobile ? 10 : 12
        }
      }
    },
    grid: {
      line: {
        stroke: colors.grey[700],
        strokeWidth: 1
      }
    },
    tooltip: {
      container: {
        background: colors.primary[700],
        color: colors.grey[100],
        fontSize: isMobile ? 10 : 12,
      }
    }
  };

  // Format numbers for display (compact for UI)
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  // Format numbers for tooltip (more precision)
  const formatTooltipNumber = (value) => {
    if (value >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(3)}B`;
    } else if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(2)}M`;
    } else if (value >= 1_000) {
      return `${(value / 1_000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  // Format axis values
  const formatAxisValue = (value) => {
    if (value >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(2)}B`;
    }
    if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(0)}M`;
    }
    if (value >= 1_000) {
      return `${(value / 1_000).toFixed(0)}K`;
    }
    return value.toString();
  };

  // Filter data based on selected time range
  const filterDataByTime = (data, days) => {
    if (days === 'all') return data;
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    
    return data.filter(entry => {
      const entryDate = new Date(entry.rawDate);
      return entryDate >= cutoffDate;
    });
  };

  // Fetch Total Supply history
  const fetchSupplyHistory = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get('https://api.elmo-burns.com/api/v1/dec-distribution-history/');
      
      if (response.data && response.data.results && response.data.results.length > 0) {
        // Sort by date ascending (oldest to newest)
        const sortedData = [...response.data.results].reverse();
        
        // Map total supply data
        const allData = sortedData.map(entry => ({
          x: entry.date,
          y: parseFloat(entry.total_supply),
          rawDate: entry.date
        }));
        
        setFullData(allData);
        setError(null);
      } else {
        throw new Error('No supply history data available');
      }
      
    } catch (err) {
      console.error('Failed to fetch supply history:', err);
      setError('Unable to load DEC supply history');
      setFullData([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Update chart data when filter or full data changes
  useEffect(() => {
    if (fullData.length > 0) {
      const days = timeFilter === 'all' ? 'all' : parseInt(timeFilter);
      const filteredData = filterDataByTime(fullData, days);
      
      setChartData([{
        id: 'Total Supply',
        color: colors.blueAccent[500],
        data: filteredData
      }]);
    }
  }, [timeFilter, fullData]);
  
  useEffect(() => {
    fetchSupplyHistory();
    
    // Refresh every 5 minutes
    const interval = setInterval(fetchSupplyHistory, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!chartData || chartData.length === 0 || !chartData[0].data || chartData[0].data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography>No supply data available</Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
    >
      {/* Filter Buttons */}
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <ButtonGroup size="small" variant="outlined" sx={{ bgcolor: colors.primary[500] }}>
          <Button 
            onClick={() => setTimeFilter('7')}
            sx={{
              bgcolor: timeFilter === '7' ? colors.blueAccent[700] : 'transparent',
              color: timeFilter === '7' ? colors.grey[100] : colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': {
                bgcolor: colors.blueAccent[800],
                borderColor: colors.grey[600],
              }
            }}
          >
            7D
          </Button>
          <Button 
            onClick={() => setTimeFilter('14')}
            sx={{
              bgcolor: timeFilter === '14' ? colors.blueAccent[700] : 'transparent',
              color: timeFilter === '14' ? colors.grey[100] : colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': {
                bgcolor: colors.blueAccent[800],
                borderColor: colors.grey[600],
              }
            }}
          >
            14D
          </Button>
          <Button 
            onClick={() => setTimeFilter('30')}
            sx={{
              bgcolor: timeFilter === '30' ? colors.blueAccent[700] : 'transparent',
              color: timeFilter === '30' ? colors.grey[100] : colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': {
                bgcolor: colors.blueAccent[800],
                borderColor: colors.grey[600],
              }
            }}
          >
            30D
          </Button>
          <Button 
            onClick={() => setTimeFilter('all')}
            sx={{
              bgcolor: timeFilter === 'all' ? colors.blueAccent[700] : 'transparent',
              color: timeFilter === 'all' ? colors.grey[100] : colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': {
                bgcolor: colors.blueAccent[800],
                borderColor: colors.grey[600],
              }
            }}
          >
            ALL
          </Button>
        </ButtonGroup>
      </Box>

      {/* Chart */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveLine
          data={chartData}
          theme={customChartTheme}
          margin={{ top: 20, right: isMobile ? 20 : 50, bottom: isMobile ? 50 : 60, left: isMobile ? 50 : 80 }}
          xScale={{
            type: 'time',
            format: '%Y-%m-%d',
            precision: 'day',
          }}
          xFormat="time:%Y-%m-%d"
          yScale={{
            type: 'linear',
            min: 'auto',
            max: 'auto',
          }}
          yFormat={(value) => formatAxisValue(value)}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            format: '%b %d',
            tickRotation: isMobile ? -45 : 0,
            legend: 'Date',
            legendOffset: isMobile ? 40 : 50,
            legendPosition: 'middle',
            tickSize: 5,
            tickPadding: 5,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Total Supply',
            legendOffset: isMobile ? -45 : -70,
            legendPosition: 'middle',
            format: formatAxisValue,
          }}
          colors={(d) => d.color}
          pointSize={isMobile ? 6 : 8}
          pointColor={{ theme: 'background' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'serieColor' }}
          pointLabelYOffset={-12}
          enableArea={true}
          areaOpacity={0.1}
          useMesh={true}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            return (
              <Box
                sx={{
                  background: colors.primary[700],
                  padding: '9px 12px',
                  border: `1px solid ${colors.primary[600]}`,
                  borderRadius: '4px',
                }}
              >
                <Typography variant="body2" sx={{ color: colors.grey[100], mb: 0.5, fontWeight: 'bold' }}>
                  {new Date(slice.points[0].data.x).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </Typography>
                {slice.points.map((point) => (
                  <Box key={point.id} display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: point.serieColor,
                        borderRadius: '50%',
                      }}
                    />
                    <Typography variant="body2" sx={{ color: colors.grey[100] }}>
                      <strong>{point.serieId}:</strong> {formatTooltipNumber(point.data.y)} DEC
                    </Typography>
                  </Box>
                ))}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default DecSupplyChart;
